<template>
  <div class="training-topic-view pa-4">
    <!-- Header Neutro -->
    <v-card class="mb-4 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="d-flex align-center py-4 px-5 flex-wrap">
        <v-icon color="primary" large class="mr-3">mdi-book-open-page-variant-outline</v-icon>
        <div>
          <h2 class="text-h6 font-weight-black grey--text text--darken-4 mb-0">
            Temas de Capacitación
          </h2>
          <span class="text-caption grey--text text--darken-2">
            Catálogo maestro de temas, tiempos de vigencia reglamentarios y requisitos de certificación.
          </span>
        </div>
      </v-card-title>
    </v-card>

    <!-- Filtros de Búsqueda -->
    <v-card class="mb-5 rounded-xl border-light" outlined elevation="0">
      <v-card-title class="py-3 px-5 d-flex align-center flex-wrap">
        <v-icon color="grey darken-3" small class="mr-2">mdi-filter-variant</v-icon>
        <span class="text-subtitle-2 font-weight-bold grey--text text--darken-4 text-uppercase">
          Filtros de Búsqueda
        </span>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="py-4 px-5">
        <v-row dense align="center">
          <v-col cols="12" sm="4" md="3">
            <s-select-definition :def="1067" label="Tipo de Instrucción" v-model="filters.TypeInstruction" clearable />
          </v-col>
          <v-col cols="12" sm="4" md="4">
            <s-select-area label="Área Responsable" v-model="filters.AreID" clearable />
          </v-col>
          <v-col cols="12" sm="4" md="3">
            <s-text v-model="filters.Search" label="Buscar tema o descripción..." clearable />
          </v-col>
          <v-col cols="12" sm="4" md="2">
            <v-btn block color="primary" class="rounded-lg font-weight-bold white--text text-none py-4" elevation="0" :loading="loading" @click="fetchTopics">
              <v-icon left small>mdi-magnify</v-icon>
              Consultar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Grilla de Datos -->
    <v-card class="rounded-xl border-light bg-white pa-4 mb-5" outlined elevation="0">
      <div class="d-flex align-center justify-space-between mb-3 flex-wrap">
        <div class="d-flex align-center">
          <v-icon color="grey darken-3" class="mr-2">mdi-format-list-bulleted</v-icon>
          <span class="text-subtitle-1 font-weight-black grey--text text--darken-4 text-uppercase">
            Lista de Temas ({{ topics.length }})
          </span>
        </div>
        <v-btn color="primary" class="rounded-lg text-none font-weight-bold px-4 py-4" small elevation="0" @click="openCreateModal">
          <v-icon left small>mdi-plus</v-icon>
          Nuevo Tema
        </v-btn>
      </div>

      <v-data-table
        :headers="headers"
        :items="topics"
        :loading="loading"
        dense
        class="border-rounded elevation-0"
        :items-per-page="15"
      >
        <template v-slot:[`item.TypeInstructionName`]="{ item }">
          <v-chip small outlined color="primary" class="font-weight-bold">
            {{ item.TypeInstructionName || 'Inducción' }}
          </v-chip>
        </template>

        <template v-slot:[`item.TtpDurationMinutes`]="{ item }">
          <span class="font-weight-medium">{{ item.TtpDurationMinutes ? item.TtpDurationMinutes + ' min' : '-' }}</span>
        </template>

        <template v-slot:[`item.TtpValidityMonths`]="{ item }">
          <span class="font-weight-medium">{{ item.TtpValidityMonths ? item.TtpValidityMonths + ' meses' : 'Permanente' }}</span>
        </template>

        <template v-slot:[`item.TtpRequiresRenewal`]="{ item }">
          <v-chip x-small :color="item.TtpRequiresRenewal === 1 ? 'info' : 'grey'" dark>
            {{ item.TtpRequiresRenewal === 1 ? 'Sí' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:[`item.TtpRequiresCertificate`]="{ item }">
          <v-chip x-small :color="item.TtpRequiresCertificate === 1 ? 'success' : 'grey'" dark>
            {{ item.TtpRequiresCertificate === 1 ? 'Sí' : 'No' }}
          </v-chip>
        </template>

        <template v-slot:[`item.actions`]="{ item }">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click="openEditModal(item)">
                <v-icon small>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <span>Editar</span>
          </v-tooltip>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="deactivateTopic(item)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
            <span>Desactivar</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal Formulario -->
    <TrainingTopicFormModal
      v-model="modal.show"
      :edit-item="modal.item"
      @saved="fetchTopics"
    />
  </div>
</template>

<script>
import SText from "@/components/Utils/SText.vue";
import SSelectArea from "@/components/Utils/SSelectArea.vue";
import SSelectDefinition from "@/components/Utils/SSelectDefinition.vue";
import TrainingTopicService from "@/services/Management/TrainingTopicService";
import TrainingTopicFormModal from "./TrainingTopicFormModal.vue";

export default {
  name: "TrainingTopicList",
  components: {
    SText,
    SSelectArea,
    SSelectDefinition,
    TrainingTopicFormModal,
  },
  data() {
    return {
      loading: false,
      filters: {
        TypeInstruction: null,
        AreID: null,
        Search: "",
        SecStatus: 1,
      },
      topics: [],
      headers: [
        { text: "ID", value: "TtpID", align: "center", width: "70px" },
        // { text: "Tipo", value: "TypeInstructionName", width: "130px" },
        { text: "Tema", value: "TtpTitle", align: "start" },
        // { text: "Área Responsable", value: "AreName", width: "180px" },
        { text: "Duración", value: "TtpDurationMinutes", align: "center", width: "100px" },
        { text: "Vigencia", value: "TtpValidityMonths", align: "center", width: "110px" },
        { text: "Renovación", value: "TtpRequiresRenewal", align: "center", width: "100px" },
        { text: "Certificado", value: "TtpRequiresCertificate", align: "center", width: "100px" },
        { text: "Acciones", value: "actions", sortable: false, align: "center", width: "110px" },
      ],
      modal: {
        show: false,
        item: null,
      },
    };
  },
  created() {
    this.fetchTopics();
  },
  methods: {
    fetchTopics() {
      this.loading = true;
      const reqId = this.$fun.getUserID();
      TrainingTopicService.list(this.filters, reqId)
        .then((res) => {
          this.topics = Array.isArray(res.data) ? res.data : [];
        })
        .catch(() => {
          this.$fun.alert("Error al cargar la lista de temas.", "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openCreateModal() {
      this.modal.item = null;
      this.modal.show = true;
    },
    openEditModal(item) {
      this.modal.item = Object.assign({}, item);
      this.modal.show = true;
    },
    deactivateTopic(item) {
      this.$fun.alert("¿Está seguro de desactivar este tema de capacitación?", "question").then((r) => {
        if (r && r.value) {
          const reqId = this.$fun.getUserID();
          const payload = Object.assign({}, item, { SecStatus: 0 });
          TrainingTopicService.save(payload, reqId)
            .then(() => {
              this.$fun.alert("Tema desactivado correctamente.", "success");
              this.fetchTopics();
            })
            .catch(() => {
              this.$fun.alert("Error al desactivar el tema.", "error");
            });
        }
      });
    },
  },
};
</script>
